/**
 * Transforms form data from the prebooking wizard into the API format
 * expected by the backend prebooking service
 */

import validator from 'validator';
import i18n from '../i18n';
import moment from 'moment';
import { isHeroBrandEnabled } from './heroBrandUtils';
import { isBeyondMaxBookingDate } from './timeUtils';

/**
 * Transform formData and a single time slot into format for createPrebooking service
 * @param {Object} formData - The complete form data from usePrebookingState
 * @param {Object} timeSlot - A single time slot object { date, startTime, duration, endTime }
 * @returns {Object} Prebooking data formatted for the createPrebooking service
 */
export const transformForAPI = (formData, timeSlot) => {
  // Combine date and startTime into a single datetime
  const combinedDateTime = new Date(timeSlot.date);
  if (timeSlot.startTime) {
    combinedDateTime.setHours(timeSlot.startTime.getHours());
    combinedDateTime.setMinutes(timeSlot.startTime.getMinutes());
    combinedDateTime.setSeconds(0);
    combinedDateTime.setMilliseconds(0);
  }

  // Build skills array combining competence levels, dialect, and gender
  // Filter to only include valid UUIDs to prevent backend validation errors
  const selectedSkillIds = [
    ...(formData.selectedDialect ? [formData.selectedDialect] : []),
    ...(formData.gender && formData.gender !== 'does_not_matter' ? [formData.gender] : []),
    ...(formData.experienceSkill ? [formData.experienceSkill] : []),
  ].filter(id => id && validator.isUUID(id));

  // Build customerServiceComments with onsite fallback handling (system-generated notes only)
  let customerServiceComments = '';

  // Determine callType and presence. The interpretation mode (call/video/
  // conference) is only user-selectable in the remote view, so it must only
  // influence callType/presence when presence === 'remote'. Otherwise a stale
  // mode left over from the remote view (e.g. the user picked Conference and
  // then switched the presence tab to On-site) would wrongly turn the booking
  // into a conference (presence => external).
  let presence = formData.presence;
  let callType;

  if (presence === 'remote') {
    if (formData.mode === 'conference') {
      // Conference -> data + external
      callType = 'data';
      presence = 'external';
    } else if (formData.mode === 'call') {
      // Phone call -> call + external
      callType = 'call';
      presence = 'external';
    } else {
      // Video call
      callType = 'data';
    }
  } else if (presence === 'notice') {
    callType = 'call';
  } else {
    // onsite (or any non-remote): mode is not applicable. The interpreter is
    // physically present, so callType is not user-selectable here; default to
    // 'call' (never the stale remote 'mode', which could be the invalid
    // 'conference').
    callType = 'call';
  }

  // Build base prebooking object
  const prebookData = {
    // Date and time
    date: combinedDateTime,
    duration: timeSlot.duration, // Keep as minutes

    // Languages
    selectedLanguageId: formData.selectedLanguageId,
    selectedTargetLanguageId: formData.selectedTargetLanguageId,
    contractId: formData.selectedContractId || null,

    // Skills (array)
    selectedSkillIds,

    // Presence and mode
    callType,
    presence,

    // Invoicing
    invoiceUserGroupId: formData.costAllocation !== 'other'
      ? formData.costAllocation
      : (formData.costAllocationGroup?.id || null),
    invoiceRef: formData.additionalInvoicingInfo || null,
    comment: formData.additionalInfoForHandler || null,

    // Emails
    ccEmails: formData.ccEmails && formData.ccEmails.length > 0
      ? formData.ccEmails.join(',')
      : null,

    // Client and subject
    patientName: formData.clientName || null,
    subject: formData.subject || null,
    externalOrderId: formData.externalOrderId || null,

    // Preferred interpreters (array)
    favoriteInterpreters: formData.preferredInterpreters || [],

    // Allowed interpreter levels with sort order
    allowedLevelJoins: formData.competenceLevel && formData.competenceLevel.length > 0
      ? formData.competenceLevel.filter(level => level !== 'other_interpreter').map((levelId, index) => ({
          levelId,
          sortOrder: index,
        }))
      : undefined,
  };

  // Add presence-specific fields
  if (formData.presence === 'onsite') {
    prebookData.streetAddress = formData.streetAddress || null;
    prebookData.postcode = formData.postCode || null;
    prebookData.locationCity = formData.city || null;
    prebookData.messageToInterpreter = formData.arrivalInstruction || null;

    // Handle onsite fallback preference
    if (formData.onsiteFallbackHandling === 'cancel_booking') {
      const cancelNote = 'CANCEL PREBOOKING IF NO ONSITE INTERPRETER AVAILABLE';
      customerServiceComments = customerServiceComments
        ? `${customerServiceComments}\n${cancelNote}`
        : cancelNote;
    } else if (formData.onsiteFallbackHandling === 'assign_remote_interpreter') {
      const remoteNote = 'USE REMOTE INTERPRETATION IF NO ONSITE INTERPRETER AVAILABLE';
      customerServiceComments = customerServiceComments
        ? `${customerServiceComments}\n${remoteNote}`
        : remoteNote;
    }

    // Add distance limit preference if specified
    if (formData.acceptBeyondKmLimit) {
      if (formData.kmBeyondLimit === 'unlimited') {
        // 0 bypasses the distance filter on the backend
        prebookData.distanceLimitOverrideMeters = 0;
        const limitNote = 'ACCEPTS INTERPRETERS WITHOUT DISTANCE LIMIT';
        customerServiceComments = customerServiceComments
          ? `${customerServiceComments}\n${limitNote}`
          : limitNote;
      } else {
        const contractLimit = formData.contractDistanceLimit || 0;
        const totalDistance = contractLimit + (Number(formData.kmBeyondLimit) || 0);
        prebookData.distanceLimitOverrideMeters = totalDistance * 1000;
        const limitNote = `ACCEPTS INTERPRETERS WITHIN ${totalDistance} KM`;
        customerServiceComments = customerServiceComments
          ? `${customerServiceComments}\n${limitNote}`
          : limitNote;
      }
    }
  }

  // Add conference-specific fields (only for a remote conference booking, so a
  // stale videoMeetingUrl doesn't clobber the on-site arrival instruction).
  if (formData.presence === 'remote' && formData.mode === 'conference' && formData.videoMeetingUrl) {
    prebookData.messageToInterpreter = formData.videoMeetingUrl;
  }

  // Add announcement-specific fields
  if (formData.presence === 'notice') {
    // Format messageToInterpreter with contact info and message
    const additionalPhones = (formData.additionalPhoneNumbers || [])
      .filter(phone => phone?.trim())
      .join(' ');

    const phoneNumbers = [formData.mainPhoneNumber, additionalPhones]
      .filter(p => p?.trim())
      .join(' ');

    prebookData.messageToInterpreter = [
      formData.alternativeContactPerson
        ? `${i18n('relay_the_message_to_person')} ${formData.alternativeContactPerson}`
        : '',
      `${i18n('numbers_to_be_called')} ${phoneNumbers}`,
      `${i18n('message')}`,
      formData.messageContent || ''
    ].filter(line => line).join('\n');

    // For announcements, invoiceRef uses customer name and birthday only for non-Hero brands
    if (!isHeroBrandEnabled()) {
      prebookData.invoiceRef = `${formData.customerName || ''} ${i18n('birthday')} ${
        formData.dateOfBirth
          ? moment(formData.dateOfBirth).format('DD/MM/YYYY')
          : ''
      }`;
    }
  }

  // Add excluded interpreters if any
  if (formData.excludedInterpreters && formData.excludedInterpreters.length > 0) {
    const excludeNote = `EXCLUDE INTERPRETERS: ${formData.excludedInterpreters.join(', ')}`;
    customerServiceComments = customerServiceComments
      ? `${customerServiceComments}\n${excludeNote}`
      : excludeNote;
  }

  // Accept alternative language (structured field)
  if (formData.acceptAlternativeLanguage === true) {
    prebookData.acceptAlternativeLanguage = true;
    if (formData.alternativeLanguageId) {
      prebookData.alternativeLanguageId = formData.alternativeLanguageId;
    }
  }

  // Add note for "other_interpreter" (unqualified interpreter) selection
  if (formData.competenceLevel && formData.competenceLevel.includes('other_interpreter')) {
    const otherInterpreterNote = 'REQUESTED LEVEL INCLUDES: Unqualified interpreter (other_interpreter)';
    if (!customerServiceComments.includes(otherInterpreterNote)) {
    customerServiceComments = customerServiceComments
        ? `${customerServiceComments}\n${otherInterpreterNote}`
        : otherInterpreterNote;
    }
  }

  // Remote backup connection type (structured field)
  if (formData.presence === 'onsite' &&
      formData.onsiteFallbackHandling === 'assign_remote_interpreter' &&
      formData.backupCallTypeForOnSite) {
    // Handle conference backup - transform to data + external (like main conference mode)
    if (formData.backupCallTypeForOnSite === 'conference') {
      prebookData.backupCallTypeForOnSite = 'data';
      prebookData.backupPresenceForOnSite = 'external';
    } else {
      prebookData.backupCallTypeForOnSite = formData.backupCallTypeForOnSite;
      prebookData.backupPresenceForOnSite = formData.backupCallTypeForOnSite === 'call' ? 'external' : 'remote';
    }
  }

  // VAT selection
  if (formData.vatIncluded !== undefined) {
    prebookData.vatIncluded = formData.vatIncluded;
  }

  // Set customerServiceComments
  prebookData.customerServiceComments = customerServiceComments || null;

  return prebookData;
};

/**
 * Transform all form data for multiple time slots
 * @param {Object} formData - The complete form data from usePrebookingState
 * @returns {Array} Array of prebooking objects ready for API submission
 */
export const transformAllTimeSlots = (formData) => {
  // Handle announcements (notice) specially - they use lastDeliveryDate instead of timeSlots
  if (formData.presence === 'notice') {
    if (!formData.lastDeliveryDate) {
      throw new Error('Last delivery date is required for announcements');
    }

    // Create a pseudo time slot for the announcement with default time 18:00
    const announcementDateTime = new Date(formData.lastDeliveryDate);
    announcementDateTime.setHours(18, 0, 0, 0);

    const pseudoTimeSlot = {
      date: announcementDateTime,
      startTime: announcementDateTime,
      // Duration based on message content length (like ChatWizard)
      duration: (formData.messageContent?.length || 0) <= 500 ? 15 : 30,
      endTime: null
    };

    return [transformForAPI(formData, pseudoTimeSlot)];
  }

  // Regular flow for remote and onsite bookings
  if (!formData.timeSlots || formData.timeSlots.length === 0) {
    throw new Error('No time slots provided');
  }

  return formData.timeSlots.map(slot => transformForAPI(formData, slot));
};

/**
 * Validate form data before submission
 * @param {Object} formData - The complete form data from usePrebookingState
 * @returns {Array} Array of error messages (empty if valid)
 */
export const validateFormData = (formData) => {
  const errors = [];

  // Required fields for all types
  if (!formData.selectedLanguageId) {
    errors.push(i18n('language_required'));
  }

  // For announcements, validate lastDeliveryDate instead of timeSlots
  if (formData.presence === 'notice') {
    if (!formData.lastDeliveryDate) {
      errors.push(i18n('date_required'));
    } else if (isBeyondMaxBookingDate(formData.lastDeliveryDate)) {
      // TUL-5698: transformAllTimeSlots turns this into interpretation_date
      errors.push(i18n('date_too_far_in_future'));
    }
  } else {
    // For remote and onsite, validate timeSlots
    if (!formData.timeSlots || formData.timeSlots.length === 0) {
      errors.push(i18n('at_least_one_time_slot_required'));
    }

    // Emails are optional - no validation required

    // Validate time slots
    formData.timeSlots?.forEach((slot) => {
      if (!slot.date) {
        errors.push(i18n('date_required'));
      } else if (isBeyondMaxBookingDate(slot.date)) {
        // TUL-5698: last gate before handleSubmit posts the order
        errors.push(i18n('date_too_far_in_future'));
      }
      if (!slot.startTime) {
        errors.push(i18n('time_required'));
      }
      if (!slot.duration) {
        errors.push(i18n('duration_required'));
      }
    });
  }

  // Remote-specific validation
  if (formData.presence === 'remote') {
    if (!formData.mode) {
      errors.push(i18n('call_type_required'));
    }
  }

  // On-site specific validation
  if (formData.presence === 'onsite') {
    if (!formData.streetAddress) {
      errors.push(i18n('street_address_required'));
    }
    if (!formData.city) {
      errors.push(i18n('city_required'));
    }
    if (!formData.postCode) {
      errors.push(i18n('post_code_required'));
    }
  }

  // Announcement-specific validation - matches PrebookingInvoicingStep.js logic
  if (formData.presence === 'notice') {
    if (!formData.messageContent) {
      errors.push(i18n('message_content_required'));
    }
    // Customer name and DOB only required for non-staging environments (Hero brand check)
    if (!isHeroBrandEnabled()) {
      if (!formData.customerName?.trim()) {
        errors.push(i18n('customer_name_required'));
      }
      if (!formData.dateOfBirth) {
        errors.push(i18n('date_of_birth_required'));
      }
    }
    // Main phone number always required for announcements
    if (!formData.mainPhoneNumber?.trim()) {
      errors.push(i18n('phone_number_required'));
    }
  }

  // Skills validation - ensure valid UUIDs (silent validation, no user-facing errors)
  // These are filtered out during transform if invalid

  return errors;
};

/**
 * Transform API prebooking data back into wizard form state
 * @param {Object} prebooking - Prebooking object from the API (getPrebookings response)
 * @param {Object} options - Optional configuration
 * @param {number} options.contractDistanceLimit - Contract's on-site km limit (default: 70)
 * @returns {Object} Partial form data to merge into wizard state
 */
export const transformFromAPI = (prebooking, options = {}) => {
  if (!prebooking) return {};

  const { contractDistanceLimit = 0 } = options;
  // Reverse presence/callType → mode + presence
  let presence = prebooking.presence || 'remote';
  let mode = prebooking.callType || 'data';

  if (presence === 'external' && prebooking.callType === 'data') {
    // Could be conference or video remote
    mode = 'conference';
    presence = 'remote';
  } else if (presence === 'external' && prebooking.callType === 'call') {
    mode = 'call';
    presence = 'remote';
  } else if (presence === 'notice') {
    mode = 'call'; // default for announcements
  }

  // Build time slot from interpretationDate + durationEstimated
  const interpretationDate = prebooking.interpretationDate
    ? new Date(prebooking.interpretationDate)
    : null;
  const durationMinutes = prebooking.durationEstimated
    ? prebooking.durationEstimated / 60
    : null;

  let startTime = null;
  let endTime = null;
  if (interpretationDate) {
    startTime = new Date(interpretationDate);
    if (durationMinutes) {
      endTime = new Date(interpretationDate.getTime() + durationMinutes * 60000);
    }
  }

  const timeSlots = interpretationDate
    ? [{
        date: interpretationDate,
        startTime,
        duration: durationMinutes,
        endTime,
      }]
    : [{ date: null, startTime: null, duration: null, endTime: null }];

  // Parse ccEmails from comma-separated string
  const ccEmails = prebooking.ccEmails
    ? prebooking.ccEmails.split(',').map(e => e.trim()).filter(Boolean)
    : [];

  // Parse competence levels from allowedLevelJoins
  const competenceLevel = prebooking.allowedLevelJoins
    ? prebooking.allowedLevelJoins
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map(l => l.levelId)
    : [];

  // Parse customerServiceComments for structured data
  const comments = prebooking.customerServiceComments || '';
  let onsiteFallbackHandling = null;
  let acceptBeyondKmLimit = false;
  let kmBeyondLimit = null;
  let excludedInterpreters = [];
  let additionalInfoForHandler = '';

  const commentLines = comments.split('\n').filter(Boolean);
  const structuredLines = [];

  for (const line of commentLines) {
    if (line === 'CANCEL PREBOOKING IF NO ONSITE INTERPRETER AVAILABLE') {
      onsiteFallbackHandling = 'cancel_booking';
      structuredLines.push(line);
    } else if (line === 'USE REMOTE INTERPRETATION IF NO ONSITE INTERPRETER AVAILABLE') {
      onsiteFallbackHandling = 'assign_remote_interpreter';
      structuredLines.push(line);
    } else if (line.startsWith('ACCEPTS INTERPRETERS WITHIN ')) {
      acceptBeyondKmLimit = true;
      const match = line.match(/ACCEPTS INTERPRETERS WITHIN (\d+) KM/);
      if (match) {
        kmBeyondLimit = parseInt(match[1], 10);
      }
      structuredLines.push(line);
    } else if (line === 'ACCEPTS INTERPRETERS WITHOUT DISTANCE LIMIT') {
      acceptBeyondKmLimit = true;
      kmBeyondLimit = 'unlimited';
      structuredLines.push(line);
    } else if (line.startsWith('EXCLUDE INTERPRETERS: ')) {
      excludedInterpreters = line.replace('EXCLUDE INTERPRETERS: ', '').split(', ').filter(Boolean);
      structuredLines.push(line);
    } else if (line === 'ACCEPTS RUSSIAN SPEAKING INTERPRETER AS FALLBACK FOR UKRAINIAN') {
      structuredLines.push(line);
    } else {
      // Regular additional info
    }
  }

  // Use structured distanceLimitOverrideMeters field when available
  // The API stores total distance (contract limit + extra), so we need to subtract contract limit
  // to get the extra km beyond limit that the user selected.
  // distanceLimitMeters === 0 means the user chose unlimited (no distance filter).
  if (prebooking.distanceLimitMeters === 0) {
    acceptBeyondKmLimit = true;
    kmBeyondLimit = 'unlimited';
  } else if (prebooking.distanceLimitMeters) {
    acceptBeyondKmLimit = true;
    const totalDistanceKm = prebooking.distanceLimitMeters / 1000;
    kmBeyondLimit = Math.max(0, totalDistanceKm - contractDistanceLimit);
  }

  // Read additional info from customerComments (correct field).
  // Fall back to non-structured lines in customerServiceComments for old prebookings.
  const legacyAdditionalInfo = commentLines
    .filter(line => !structuredLines.includes(line))
    .join('\n');
  additionalInfoForHandler = prebooking.customerComments || legacyAdditionalInfo;

  // Parse messageToInterpreter based on presence
  let arrivalInstruction = '';
  let videoMeetingUrl = '';
  let alternativeContactPerson = '';
  let mainPhoneNumber = '';
  let additionalPhoneNumbers = [];
  let messageContent = '';

  if (presence === 'onsite') {
    arrivalInstruction = prebooking.messageToInterpreter || '';
  } else if (mode === 'conference') {
    videoMeetingUrl = prebooking.messageToInterpreter || '';
  } else if (presence === 'notice') {
    // Parse notice messageToInterpreter format
    const msgLines = (prebooking.messageToInterpreter || '').split('\n');
    for (let i = 0; i < msgLines.length; i++) {
      const line = msgLines[i];
      if (line.startsWith(i18n('relay_the_message_to_person') + ' ')) {
        alternativeContactPerson = line.replace(i18n('relay_the_message_to_person') + ' ', '');
      } else if (line.startsWith(i18n('numbers_to_be_called') + ' ')) {
        const phones = line.replace(i18n('numbers_to_be_called') + ' ', '').trim().split(' ').filter(Boolean);
        if (phones.length > 0) {
          mainPhoneNumber = phones[0];
          additionalPhoneNumbers = phones.slice(1);
        }
      } else if (line === i18n('message')) {
        // Next lines are message content
        messageContent = msgLines.slice(i + 1).join('\n');
        break;
      }
    }
  }

  // Parse invoiceRef for notice: "customerName birthday DD/MM/YYYY"
  let customerName = '';
  let dateOfBirth = null;
  if (presence === 'notice' && prebooking.invoiceRef) {
    const birthdayLabel = ` ${i18n('birthday')} `;
    const idx = prebooking.invoiceRef.indexOf(birthdayLabel);
    if (idx !== -1) {
      customerName = prebooking.invoiceRef.substring(0, idx).trim();
      const dateStr = prebooking.invoiceRef.substring(idx + birthdayLabel.length).trim();
      if (dateStr) {
        const parsed = moment(dateStr, 'DD/MM/YYYY');
        if (parsed.isValid()) {
          dateOfBirth = parsed.toDate();
        }
      }
    }
  }

  // Extract skills: dialect, gender, experience from prebooking.skills array
  let selectedDialect = null;
  let gender = null;
  let experienceSkill = null;
  if (prebooking.skills && Array.isArray(prebooking.skills)) {
    for (const skill of prebooking.skills) {
      const tag = skill.category?.tag;
      if (tag === 'dialect') {
        selectedDialect = skill.id;
      } else if (tag === 'gender' || tag === 'hero-gen') {
        gender = skill.id;
      } else {
        experienceSkill = skill.id;
      }
    }
  }

  // Reverse transform backupCallTypeForOnSite: data + external = conference
  let backupCallTypeForOnSite = prebooking.backupCallTypeForOnSite || null;
  if (backupCallTypeForOnSite === 'data' && prebooking.backupPresenceForOnSite === 'external') {
    backupCallTypeForOnSite = 'conference';
  }

  const result = {
    presence,
    mode,
    selectedLanguageId: prebooking.interpretedLanguage?.id || null,
    selectedTargetLanguageId: prebooking.targetLanguage?.id || null,
    selectedContractId: prebooking.contractId || prebooking.contract?.id || null,
    selectedContractName: prebooking.contract?.name || prebooking.contractName || '',
    targetLanguageName: prebooking.targetLanguage?.name || null,
    timeSlots,
    ccEmails,
    competenceLevel,
    costAllocation: prebooking.invoiceUserGroupId || prebooking.invoiceUserGroup?.id || null,
    costAllocationGroup: prebooking.invoiceUserGroup || null,
    additionalInvoicingInfo: (presence !== 'notice' || isHeroBrandEnabled())
      ? (prebooking.invoiceRef || '')
      : '',
    clientName: prebooking.patientName || '',
    subject: prebooking.subject || '',
    externalOrderId: prebooking.externalOrderId || '',
    streetAddress: prebooking.streetAddress || '',
    postCode: prebooking.postcode || '',
    city: prebooking.locationCity || '',
    arrivalInstruction,
    videoMeetingUrl,
    additionalInfoForHandler,
    onsiteFallbackHandling,
    acceptBeyondKmLimit,
    kmBeyondLimit,
    contractDistanceLimit,
    excludedInterpreters,
    selectedDialect,
    gender,
    experienceSkill,
    // Notice-specific fields
    alternativeContactPerson,
    mainPhoneNumber,
    additionalPhoneNumbers,
    messageContent,
    customerName,
    dateOfBirth,
    // Structured backend fields
    acceptAlternativeLanguage: prebooking.acceptAlternativeLanguage || false,
    alternativeLanguageId: prebooking.alternativeLanguageId || null,
    backupCallTypeForOnSite,
    // VAT selection
    vatIncluded: prebooking.vatIncluded !== undefined ? prebooking.vatIncluded : true,
    // Invoice and custom fields
    invoiceRecipientRef: prebooking.invoiceRecipientRef || '',
    customFields: prebooking.customFields || null,
    // Preferred interpreters
    preferredInterpreters: prebooking.favoriteInterpreterIds
      ? prebooking.favoriteInterpreterIds.split(',').filter(Boolean)
      : [],
    // Assigned interpreter info (for confirmed prebookings)
    interpreterName: prebooking.interpreter?.name || null,
    interpreterEmail: prebooking.interpreter?.email || null,
    interpreterPhone: prebooking.interpreter?.phoneNumber || prebooking.hotlinePhoneNumber || null,
    interpreterLevelNames: prebooking.interpreter?.levelNames || [],
  };

  // For announcements, set lastDeliveryDate
  if (presence === 'notice' && interpretationDate) {
    result.lastDeliveryDate = interpretationDate;
  }

  return result;
};

/**
 * Format a prebooking response for display
 * @param {Object} response - API response from creating a prebooking
 * @returns {Object} Formatted prebooking data for UI display
 */
export const formatPrebookingResponse = (response) => {
  return {
    id: response.id,
    date: new Date(response.date),
    duration: response.duration / 60, // Convert seconds back to minutes
    status: response.status,
    interpreterName: response.interpreterName,
    bookingReference: response.reference
  };
};